import { useCallback, useMemo, useState } from 'react';
import {
  useFlag as _useFlag,
  useContextMutator,
  useOpenFeatureClientStatus,
  ProviderStatus,
} from '@openfeature/react-sdk';
import type { FlagValue, EvaluationOptions, FeatureFlagContext } from './types';
import { toEvaluationContext } from './utils';

export type UseFlagReturn<T extends FlagValue> = ReturnType<typeof _useFlag<T>>['value'];

export function useFlag<T extends FlagValue>(
  flagKey: string,
  defaultValue: T,
  options?: EvaluationOptions
): UseFlagReturn<T> {
  return _useFlag(flagKey, defaultValue, options).value;
}

export interface UseFeatureFlagContextReturn {
  clearContext: () => Promise<void>;
  /** The last context successfully applied; undefined until setContext resolves, and after clearContext. */
  context: FeatureFlagContext | undefined;
  providerStatus: `${ProviderStatus}`;
  setContext: (context: FeatureFlagContext) => Promise<void>;
}

export function useFeatureFlagContext(): UseFeatureFlagContextReturn {
  const providerStatus = useOpenFeatureClientStatus();
  const { setContext: mutateContext } = useContextMutator();
  const [context, setResolvedContext] = useState<FeatureFlagContext | undefined>();

  const setContext = useCallback(
    async (newContext: FeatureFlagContext) => {
      await mutateContext(toEvaluationContext(newContext));
      setResolvedContext(newContext);
    },
    [mutateContext]
  );

  const clearContext = useCallback(async () => {
    await mutateContext({});
    setResolvedContext(undefined);
  }, [mutateContext]);

  // Memoized so the returned object is referentially stable across renders
  // where nothing actually changed — otherwise every consumer that puts this
  // return value in a dependency array re-runs on every render.
  return useMemo(
    () => ({ providerStatus, context, setContext, clearContext }),
    [providerStatus, context, setContext, clearContext]
  );
}
